import { Avatar, Box, Typography } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { tokens } from "../../../theme";
import { getGoogleCalendarUrl } from "../../stream-management/components/memorial-page/components/types/AddtoCalendarutils";

export type FeatureStatus = "overdue" | "in-progress" | "complete";

export interface FeatureItem {
  name: string;
  lead: string;
  stagingDate: string;
  liveDate: string;
  status: FeatureStatus;
  leadPicture?: string | null;
}

const STATUS_CONFIG: Record<
  FeatureStatus,
  {
    progressColor: string;
    progressPercent: number;
    label: string;
    labelColor: string;
    labelBg: string;
  }
> = {
  overdue: {
    progressColor: "#E11D48",
    progressPercent: 100,
    label: "Overdue",
    labelColor: "#E11D48",
    labelBg: "E11D48",
  },
  "in-progress": {
    progressColor: "#f59e0b",
    progressPercent: 50,
    label: "In progress",
    labelColor: "#f59e0b",
    labelBg: "f59e0b",
  },
  complete: {
    progressColor: "#10b981",
    progressPercent: 100,
    label: "Complete",
    labelColor: "#10b891",
    labelBg: "#10b981",
  },
};

const LEAD_COLORS: Record<string, string> = {
  Ruva: "#a89cf7",
  Nigel: "#9fe7c8",
  Leon: "#f7b99c",
};

interface FeatureCardProps {
  feature: FeatureItem;
  colors: ReturnType<typeof tokens>;
  onClick: () => void;
}
const FeatureCard = ({ feature, colors, onClick }: FeatureCardProps) => {
  const cfg = STATUS_CONFIG[feature.status];
  const accentColor = LEAD_COLORS[feature.lead] ?? "#9fe7f2";

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        borderRadius: "20px",
        backgroundColor: colors.primary[900],
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: colors.primary[700] + "40" },
      }}
    >
      {/* Lead Avatar */}
      <Avatar
        src={feature.leadPicture || undefined}
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          backgroundColor: accentColor + "30",
          color: accentColor,
          fontWeight: 700,
          fontSize: "0.85rem",
          border: `1.5px solid ${accentColor}`,
        }}
      >
        {!feature.leadPicture && feature.lead[0]}
      </Avatar>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Name + status badge */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.8rem",
              color: colors.primary[100],
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {feature.name}
          </Typography>
          <Box
            sx={{
              px: 0.8,
              py: "1px",
              borderRadius: "20px",
              backgroundColor: cfg.labelBg,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.6rem",
                fontWeight: 600,
                color: cfg.labelColor,
              }}
            >
              {cfg.label}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: colors.primary[300] + "30",
            my: 0.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${cfg.progressPercent}%`,
              borderRadius: 2,
              backgroundColor: cfg.progressColor,
              transition: "width 0.4s ease",
            }}
          />
        </Box>

        {/* Meta row — staging + live date + calendar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <AccessTime
              sx={{ fontSize: 11, color: colors.primary[700], flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: "0.65rem",
                color: colors.primary[700],
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {" "}
              {feature.liveDate}
            </Typography>
          </Box>
          {new Date(`${feature.liveDate}, 2026`) >=
            new Date(new Date().setHours(0, 0, 0, 0)) && (
            <Typography
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  getGoogleCalendarUrl({
                    title: `${feature.name} - Live Launch`,
                    description: `Live launch of ${feature.name}, led by ${feature.lead}`,
                    startDate: new Date(`${feature.liveDate}, 2026`),
                    endDate: new Date(
                      new Date(`${feature.liveDate}, 2026`).getTime() +
                        60 * 60 * 1000,
                    ),
                  }),
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              sx={{
                fontSize: "0.65rem",
                color: "#1976d2",
                cursor: "pointer",
                fontWeight: 600,
                flexShrink: 0,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              + calendar
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureCard;
